// Shared "Share national turnout as PNG" button. Composes the live
// national turnout value (10pm if present, else 2pm) + the delta vs
// the matching 2022 baseline into the /api/og/turnout endpoint and
// hands the result to the generic ShareButton. Used by every turnout
// chart on the homepage + /turnout page so the share image stays
// consistent across surfaces. Mount inside <FeatureSection
// sectionKey="share_buttons"> so editorial keeps the master kill
// switch.

import ShareButton from './ShareButton';
import { useElectionDayTurnout } from '../../lib/useElectionDayTurnout';
import {
  TURNOUT_2022_2PM_NATIONAL,
  TURNOUT_2022_CLOSE_NATIONAL,
} from '../../data/turnoutHistory';

interface Props {
  /** "Share" by default; charts that want a tighter affordance pass
   *  their own label (e.g. "Share chart"). */
  label?: string;
  /** Defaults to the standard pill; charts in tight headers can pass
   *  'chip' for a smaller variant. */
  variant?: 'pill' | 'chip';
}

export default function TurnoutShareButton({
  label = 'Share',
  variant = 'pill',
}: Props = {}) {
  const { data: turnout } = useElectionDayTurnout();
  const using10pm = turnout.national.pct_10pm !== null;
  const pct = using10pm ? turnout.national.pct_10pm : turnout.national.pct_2pm;
  if (pct === null) return null;
  const baseline = using10pm
    ? TURNOUT_2022_CLOSE_NATIONAL
    : TURNOUT_2022_2PM_NATIONAL;
  const delta = pct - baseline;
  const stateLabel = using10pm
    ? (turnout.label ?? 'Final national')
    : (turnout.label ?? 'Live · 2pm');
  return (
    <ShareButton
      endpoint="turnout"
      assetName="turnout"
      label={label}
      variant={variant}
      params={{
        pct: pct.toFixed(1),
        delta: delta.toFixed(1),
        label: stateLabel,
        dlabel: `vs 2022 ${using10pm ? 'final' : '2pm'}`,
      }}
    />
  );
}
